import { useI18n } from "@core/i18n/i18n";
import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { ErrorMessage } from "@ui/components/common/error-message";
import { TextInput } from "@ui/components/inputs/text-input";
import { PasswordField } from "@ui/pages/login/components/password-field";
import { RegisterAccountCreationFormData } from "@ui/pages/register/register-page";
import { REGEX_EMAIL } from "@utils/regex-utils";
import React from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import { ConsentContainer } from "@ui/pages/register/components/consent-container";
import { CountryLanguagesField } from "@ui/pages/login/components/country-languages-field";

interface RegisterAccountCreationFormProps {
	control: Control<RegisterAccountCreationFormData, object>;
	emailError: string;
	setEmailError: (val: string) => void;
	onEmailInputLeave?: (email: string) => void;
	countryLocales?: { defaultLocale?: string; locales: Map<string, string[]> };
	isCountryLanguagesFieldVisible?: boolean;
}

export const RegisterAccountCreationForm = ({
	control,
	emailError,
	setEmailError,
	onEmailInputLeave,
	countryLocales = { locales: new Map() },
	isCountryLanguagesFieldVisible = false,
}: RegisterAccountCreationFormProps): JSX.Element => {
	const { format, locale } = useI18n();
	const selectedLocale = useWatch({ control, name: "locale" }) ?? locale;

	return (
		<FormContainer>
			{isCountryLanguagesFieldVisible && (
				<CountryLanguagesField
					name="locale"
					control={control}
					locales={countryLocales.locales}
					labelStyle={{ marginTop: 16, marginBottom: 8 }}
				/>
			)}
			
			<Controller
				name="email"
				control={control}
				rules={{
					required: format("global.form.field.requiredError"),
					pattern: {
						value: REGEX_EMAIL,
						message: format("register.accountCreation.email.formatError"),
					},
					onChange: () => setEmailError(""),
				}}
				render={({ field, fieldState }) => (
					<>
						<EmailFieldLabel className="p-size-m">{format("register.accountCreation.email.label")}</EmailFieldLabel>
						<TextInput
							{...field}
							data-test={e2eTestId.registerPt.emailTextInput}
							placeholder={format("register.accountCreation.email.placeholder")}
							error={fieldState.invalid || !!emailError}
							inputMode="email"
							autoCorrect="off"
							autoCapitalize="none"
							onBlur={e => onEmailInputLeave?.(e.target.value)}
						/>
						<StyledErrorMessage>{fieldState.error?.message || emailError}</StyledErrorMessage>
					</>
				)}
			/>

			<PasswordField
				dataTest={e2eTestId.registerPt.passwordTextInput}
				control={control}
				error={false}
				name="password"
				labelStyle={{ marginTop: 16 }}
				rulesStyle={{ fontSize: "0.75rem", marginTop: 8 }}
			/>

			<ConsentContainer control={control} documentsLocale={selectedLocale} />
		</FormContainer>
	);
};

const FormContainer = styled.div`
	display: flex;
	flex-direction: column;
`;

const StyledErrorMessage = styled(ErrorMessage)`
	margin-top: 8px;
`;

const EmailFieldLabel = styled.label`
	margin-top: 16px;
	margin-bottom: 8px;
`;
